import { Terminal } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import { ClipboardAddon } from "@xterm/addon-clipboard";
import { io, type Socket } from "socket.io-client";
import { TmuxClipboardProvider } from "./clipboard-provider.js";
import { findImageItem, uploadPastedImage } from "./paste-image.js";
import { isPasteKeyEvent } from "./paste-keys.js";

export interface TerminalController {
  start(projectSlug: string): void;
  sendCommand(cmd: string): void;
  clear(): void;
  dispose(): void;
}

function showPasteToast(container: HTMLElement, text: string, isError: boolean): void {
  const toast = document.createElement("div");
  toast.className = `paste-toast${isError ? " paste-toast-error" : ""}`;
  toast.textContent = text;
  container.appendChild(toast);
  setTimeout(() => toast.remove(), 4000);
}

export function mountTerminal(container: HTMLElement): TerminalController {
  const term = new Terminal({
    cursorBlink: true,
    fontFamily: "ui-monospace, Menlo, Consolas, monospace",
    fontSize: 13,
    theme: { background: "#000000", foreground: "#d4d4d4" },
    scrollback: 5000,
  });
  const fit = new FitAddon();
  term.loadAddon(fit);
  // OSC 52 → browser clipboard. tmux (set-clipboard on) emits the copied
  // selection as an OSC 52 escape; the addon writes it via
  // navigator.clipboard.writeText(). Works in Firefox because the write
  // lands inside the transient-activation window of the mouse release.
  // Custom provider: the addon's default drops tmux's empty selection param.
  // Toast on success AND failure — a rejected writeText (e.g. lost user
  // activation) is otherwise indistinguishable from "nothing happened".
  const writeClipboard = async (text: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      showPasteToast(container, "✓ kopiert", false);
    } catch (err) {
      const name = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
      showPasteToast(container, `Copy fehlgeschlagen — ${name}`, true);
      throw err;
    }
  };
  term.loadAddon(new ClipboardAddon(undefined, new TmuxClipboardProvider(writeClipboard)));
  // Hand paste combos (Strg+V, Strg+Shift+V, Shift+Einfg) back to the
  // browser: returning false makes xterm skip the key WITHOUT preventDefault,
  // so the native paste event fires and the paste pipeline below takes over.
  // Without this, xterm sends Ctrl+V as \x16 to the PTY — on Windows paste
  // never worked at all.
  term.attachCustomKeyEventHandler((ev) => !isPasteKeyEvent(ev));

  term.open(container);
  fit.fit();

  const ro = new ResizeObserver(() => fit.fit());
  ro.observe(container);

  // Block the browser's native right-click menu inside the terminal — Claude
  // Code renders its own context menu via mouse-tracking, and the browser
  // menu otherwise pops up on top of it. Capture phase so we win even if
  // xterm itself stops propagation.
  const suppressContextMenu = (ev: MouseEvent): void => { ev.preventDefault(); };
  container.addEventListener("contextmenu", suppressContextMenu, { capture: true });

  // Copy-on-select for NATIVE selections (Shift+drag bypasses tmux mouse
  // tracking). Debounced so we write once after the drag settles, while the
  // mouse-up user activation is still fresh (Firefox requirement).
  let copyTimer: number | undefined;
  const selectionDisposable = term.onSelectionChange(() => {
    window.clearTimeout(copyTimer);
    copyTimer = window.setTimeout(() => {
      if (!term.hasSelection()) return;
      const text = term.getSelection();
      if (text) void writeClipboard(text).catch(() => { /* toast already shown */ });
    }, 150);
  });

  // Focus hardening: after clicking toolbar buttons etc. the hidden xterm
  // textarea loses focus and Ctrl/Cmd+V goes nowhere. Any click inside the
  // terminal panel (including padding outside xterm's own screen element)
  // refocuses the terminal.
  const refocus = (): void => { term.focus(); };
  container.addEventListener("mouseup", refocus);

  // Default transports are ["polling","websocket"] — start with HTTP polling
  // (works through strict reverse proxies), auto-upgrade to WebSocket if allowed.
  // Reconnection ON: Firefox throttles background tabs (and laptops sleep), the
  // ping/pong times out, the server drops the socket — without reconnect the
  // terminal silently freezes until a page reload. The tmux session survives
  // server-side, so re-emitting "start" reattaches seamlessly.
  const socket: Socket = io("/terminal", { reconnectionDelayMax: 5000 });
  let started = false;
  let activeSlug: string | null = null;

  socket.on("disconnect", () => {
    if (!started) return;
    started = false;
    term.writeln("\r\n\x1b[33m[Verbindung unterbrochen — verbinde automatisch neu …]\x1b[0m");
  });
  socket.on("connect", () => {
    // Only auto-restart after an explicit start() (activeSlug set) whose
    // session was lost to a disconnect. The initial connect is a no-op:
    // either start() hasn't run yet (activeSlug null) or its buffered
    // "start" emit is already on the wire (started true).
    connectErrorShown = false;
    if (!activeSlug || started) return;
    socket.emit("start", { projectSlug: activeSlug, cols: term.cols, rows: term.rows });
    started = true;
    term.writeln("\x1b[32m[neu verbunden]\x1b[0m");
  });

  // Image paste: capture phase beats xterm's own paste handling on the hidden
  // textarea. Text-only pastes fall through to xterm untouched (bracketed
  // paste keeps multi-line text as one block for Claude).
  const onPaste = (ev: ClipboardEvent): void => {
    const item = findImageItem(ev.clipboardData?.items);
    if (!item) return;
    ev.preventDefault();
    ev.stopImmediatePropagation();
    const file = item.getAsFile();
    if (!file || !started || !activeSlug) return;
    uploadPastedImage(activeSlug, file)
      .then((absPath) => {
        // Trailing space: complete token for Claude, prompt stays open.
        socket.emit("input", absPath + " ");
        showPasteToast(container, "Bild hochgeladen — Pfad eingefügt", false);
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err);
        showPasteToast(container, `Bild-Upload fehlgeschlagen: ${msg}`, true);
      });
  };
  container.addEventListener("paste", onPaste, { capture: true });

  socket.on("data", (d: string) => term.write(d));
  socket.on("exit", () => {
    term.writeln("\r\n\x1b[90m[session ended]\x1b[0m");
    started = false;
  });
  socket.on("error-event", (e: { message: string }) => {
    term.writeln(`\r\n\x1b[31m[error: ${e.message}]\x1b[0m`);
  });
  // Only one line per outage — with reconnection enabled this fires on every
  // failed retry and would otherwise spam the terminal.
  let connectErrorShown = false;
  socket.on("connect_error", (err) => {
    if (connectErrorShown) return;
    connectErrorShown = true;
    term.writeln(`\r\n\x1b[31m[connect error: ${err.message} — versuche weiter …]\x1b[0m`);
  });

  term.onData((data) => {
    if (started) socket.emit("input", data);
  });
  term.onResize(({ cols, rows }) => {
    if (started) socket.emit("resize", { cols, rows });
  });

  return {
    start(projectSlug: string) {
      if (started) return;
      activeSlug = projectSlug;
      socket.emit("start", { projectSlug, cols: term.cols, rows: term.rows });
      started = true;
    },
    sendCommand(cmd: string) {
      if (!started) return;
      socket.emit("input", cmd + "\n");
    },
    clear() {
      term.clear();
    },
    dispose() {
      ro.disconnect();
      window.clearTimeout(copyTimer);
      selectionDisposable.dispose();
      container.removeEventListener("contextmenu", suppressContextMenu, { capture: true });
      container.removeEventListener("paste", onPaste, { capture: true });
      container.removeEventListener("mouseup", refocus);
      socket.close();
      term.dispose();
    },
  };
}
